import { ScObjectButton, ScObjectsContainer } from "./styled";
import {
  clone,
  hexToRgb,
  getPathologyColor,
  getSpineZoneColor,
  getSpineSideColor,
} from "../../utils";

import React, { useEffect, useRef, useState, useMemo } from "react";

import { useDebouncedAddStep } from "../../hooks/useDebouncedAddStep";
import { useDebouncedState } from "../../hooks/useDebouncedState";
import {
  imageNameState,
  markupPathologyState,
  markupState,
  pixelSpacingState,
  regionState,
} from "../../markupAtoms";
import { Select } from "@bigdata/ui-kit";
import { pathologyList } from "../../../../constants";
import { Trash2 } from "react-feather";
import sides from "./sides";
import degrees from "./degrees";
import { useRecoilState } from "recoil";
import { squarePolygon } from "../../MarkupSpace/utilits";
import SelectMultiple from "./SelectMultiple";
import regionConfig from "../../../../pages/Dashboard/Validate/regionConfig";
import spineZones from "./spineZones";
import spineSides from "./spineSides";

export const ObjectsContainer: React.FC = () => {
  const [colorEditItem, setColorEditItem] = useState<number | undefined>(
    undefined
  );
  const [markupPathology, setMarkupPathology] =
    useDebouncedState(markupPathologyState);
  const [imageName] = useRecoilState(imageNameState);
  const [, setMarkup] = useRecoilState(markupState);
  const [pixelSpacing] = useRecoilState(pixelSpacingState);
  const [region] = useRecoilState(regionState);

  const addStep = useDebouncedAddStep();

  const objectsContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stopPropagation = (e: Event) => {
      e.stopPropagation();
    };

    objectsContainer?.current?.addEventListener("mousewheel", stopPropagation);

    return () => {
      objectsContainer?.current?.removeEventListener(
        "mousewheel",
        stopPropagation
      );
    };
  }, []);

  const activeIndex = useMemo(
    () => markupPathology[imageName]?.findIndex((item) => item.active === true),
    [markupPathology, imageName]
  );

  const deleteItem = (index: number) => {
    setMarkup("");
    setColorEditItem(undefined);
    const newData = markupPathology[imageName]?.filter(
      (_, key) => key !== index
    );
    addStep({
      step: "deleteItem",
      data: newData,
    });
  };

  const updateNameData = (
    editItem: number,
    newValue: Array<string | number> | string | number
  ) => {
    const newData = clone(markupPathology[imageName]);
    newData[editItem.toString()].name = newValue;
    addStep({
      step: "updateName",
      data: newData,
    });
  };

  const updateSide = (editItem: number, side: string | number) => {
    if (editItem !== undefined) {
      const newData = clone(markupPathology[imageName]);
      newData[editItem.toString()].side = side;
      addStep({
        step: "updateSide",
        data: newData,
      });
    }
  };

  const updateSpineSide = (editItem: number, side: string | number) => {
    if (editItem !== undefined) {
      const newData = clone(markupPathology[imageName]);
      newData[editItem.toString()].spineSide = side;
      addStep({
        step: "updateSide",
        data: newData,
      });
    }
  };

  const updateSpineZone = (editItem: number, zone: string | number) => {
    if (editItem !== undefined) {
      const newData = clone(markupPathology[imageName]);
      newData[editItem.toString()].zone = zone;
      addStep({
        step: "updateSide",
        data: newData,
      });
    }
  };

  const updateDegree = (editItem: number, degree: number) => () => {
    if (editItem !== undefined) {
      const newData = clone(markupPathology[imageName]);
      newData[editItem.toString()].degree = degree;
      addStep({
        step: "updateDegree",
        data: newData,
      });
    }
  };

  const updateActiveItem = (objectKey: number) => {
    setMarkupPathology((markup) => ({
      ...markup,
      [imageName]: markup[imageName]?.map((item, key) => {
        if (key === objectKey) {
          if (!item.active) {
            return { ...item, active: true };
          } else {
            return { ...item, active: false };
          }
        }
        if ("active" in item) {
          return { ...item, active: false };
        }
        return item;
      }),
    }));
  };

  useEffect(() => {
    const changePathology = (e: any) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        typeof activeIndex !== "undefined" &&
        pathologyList[region][e.key - 1]
      ) {
        e.preventDefault();
        updateNameData(activeIndex, pathologyList[region][e.key - 1].id);
      }
    };

    window.addEventListener("keydown", changePathology);

    return () => {
      window.removeEventListener("keydown", changePathology);
    };
  }, [markupPathology, imageName]);

  return (
    <div ref={objectsContainer}>
      <ScObjectsContainer>
        {markupPathology[imageName]?.map((data, key) => {
          if (data.markupType === "polygon" || data.points.length > 2) {
            const rgb = hexToRgb(
              ["spine"].includes(region)
                ? getSpineZoneColor(data.zone)
                : getPathologyColor(data.name, region)
            );
            return (
              <ScObjectButton
                key={"object-figure-" + key}
                type="polygon"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    updateActiveItem(key);
                  }
                }}
                className={"active"}
                style={{
                  boxShadow: data.active
                    ? `0px 0px 4px 2px ${
                        ["spine"].includes(region)
                          ? getSpineZoneColor(data.zone)
                          : getPathologyColor(data.name, region)
                      }`
                    : "none",
                  borderColor: ["spine"].includes(region)
                    ? getSpineZoneColor(data.zone)
                    : getPathologyColor(data.name, region),
                  backgroundColor: `rgba(${rgb?.r}, ${rgb?.g}, ${rgb?.b}, 0.1)`,
                }}
              >
                <div>
                  <span>
                    {["spine"].includes(region) ? (
                      <Select
                        optionsArr={spineZones.map((item) => ({
                          label: item.value,
                          value: item.id,
                        }))}
                        onChangeAmount={(v) => updateSpineZone(key, v)}
                        selectedValue={data?.zone}
                        style={{
                          color: getSpineZoneColor(data?.zone),
                        }}
                      />
                    ) : (
                      <Select
                        optionsArr={pathologyList[region].map((item) => ({
                          label: item.value,
                          value: item.id,
                        }))}
                        onChangeAmount={(v) => updateNameData(key, v)}
                        selectedValue={data.name}
                        style={{
                          color: getPathologyColor(data.name, region),
                        }}
                      />
                    )}
                  </span>
                  <div
                    className="control-item square-polygon"
                    style={{
                      color: ["spine"].includes(region)
                        ? getSpineZoneColor(data.zone)
                        : getPathologyColor(data.name, region),
                      borderColor: `rgba(${rgb?.r}, ${rgb?.g}, ${rgb?.b}, 0.15)`,
                      overflow: colorEditItem === key ? "visible" : "hidden",
                    }}
                  >
                    {(squarePolygon(data.points) * pixelSpacing).toFixed(2)}
                  </div>
                  <div
                    className="control-item"
                    style={{
                      borderColor: `rgba(${rgb?.r}, ${rgb?.g}, ${rgb?.b}, 0.15)`,
                      overflow: colorEditItem === key ? "visible" : "hidden",
                    }}
                  >
                    <button onClick={() => deleteItem(key)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <div
                  className="pathology-params"
                  style={{
                    borderColor: `rgba(${rgb?.r}, ${rgb?.g}, ${rgb?.b}, 0.15)`,
                  }}
                >
                  <Select
                    optionsArr={sides[region]}
                    onChangeAmount={(v) => updateSide(key, v)}
                    selectedValue={data.side}
                    style={{
                      color: ["spine"].includes(region)
                        ? getSpineZoneColor(data.zone)
                        : getPathologyColor(data.name, region),
                    }}
                  />
                  {["spine"].includes(region) ? (
                    <SelectMultiple
                      value={Array.isArray(data.name) ? data.name : []}
                      options={pathologyList[region].map((item) => ({
                        label: item.value,
                        value: item.id,
                      }))}
                      onChange={(v) => {
                        updateNameData(key, v);
                      }}
                      style={{
                        color: ["spine"].includes(region)
                          ? getSpineZoneColor(data.zone)
                          : getPathologyColor(data.name, region),
                        marginLeft: 16,
                      }}
                    />
                  ) : (
                    <div className="pathology-degrees">
                      {!Array.isArray(data.name) &&
                        !regionConfig[region].noDegree &&
                        degrees[data.name].map((degree) => (
                          <span
                            key={degree.value}
                            className={
                              "degree " +
                              (data.degree === degree.value ? "active" : "")
                            }
                            onClick={updateDegree(key, degree.value)}
                          >
                            {degree.label}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              </ScObjectButton>
            );
          } else {
            const rgb = hexToRgb(
              ["spine"].includes(region)
                ? getSpineSideColor(data.spineSide)
                : ""
            );
            return (
              <ScObjectButton
                key={"object-figure-" + key}
                type="segment"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    updateActiveItem(key);
                  }
                }}
                className={"active"}
                style={{
                  boxShadow: data.active
                    ? `0px 0px 4px 2px ${
                        ["spine"].includes(region)
                          ? getSpineSideColor(data.spineSide)
                          : ""
                      }`
                    : "none",
                  borderColor: ["spine"].includes(region)
                    ? getSpineSideColor(data.spineSide)
                    : "",
                  backgroundColor: `rgba(${rgb?.r}, ${rgb?.g}, ${rgb?.b}, 0.1)`,
                }}
              >
                <Select
                  optionsArr={spineSides}
                  onChangeAmount={(v) => updateSpineSide(key, v)}
                  selectedValue={data.spineSide}
                  style={{
                    color: getSpineSideColor(data.spineSide),
                  }}
                />
                <div
                  className="control-item"
                  style={{
                    borderColor: `rgba(${rgb?.r}, ${rgb?.g}, ${rgb?.b}, 0.15)`,
                    overflow: colorEditItem === key ? "visible" : "hidden",
                  }}
                >
                  <button onClick={() => deleteItem(key)}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </ScObjectButton>
            );
          }
        })}
        {/*{typeof addPathology !== "undefined" && (*/}
        {/*  <ModalPathology*/}
        {/*    pathology={markupData[addPathology].pathology}*/}
        {/*    setPathology={setPathology}*/}
        {/*    onClose={() => setAddPathology(undefined)}*/}
        {/*  />*/}
        {/*)}*/}
      </ScObjectsContainer>
    </div>
  );
};
